import { Button, Icon, Row, Size, Theme, Tray } from '@busybusy/webapp-react-ui';
import classNames from 'classnames';
import { ReactNode, useEffect, useState } from 'react';
import { ClassName } from 'types/ClassName';
import { getVideoTimeStringfromSeconds } from 'utils/stringUtils';
import { FileUploader, Thumbnail, ThumbnailTray } from '../../..';
import { DeleteIcon } from '../../../../assets/icons';
import { IFileWithPreview, IMediaEntryUploaderValue } from '../../../../types/FileUploader';
import { IMediaEntry, MediaEntryType } from '../../../../types/MediaEntry';
import './MediaEntryUploader.scss';

interface IMediaEntryUploaderProps {
  className?: ClassName;
  onChange: (value: IMediaEntryUploaderValue) => void;
  mediaEntries?: IMediaEntry[]; // Present already existing media entries in the header
  message?: ReactNode;
  multiple?: boolean;
}

const defaultMediaEntries: IMediaEntry[] = [];

function MediaEntryUploader(props: IMediaEntryUploaderProps) {
  const { mediaEntries = defaultMediaEntries, className, onChange, message, multiple, ...other } = props;

  const classes = classNames('media-entry-uploader', className);

  const [filesWithPreview, setFilesWithPreview] = useState<IFileWithPreview[]>([]);
  const [mediaEntriesToDelete, setMediaEntriesToDelete] = useState<IMediaEntry[]>([]);

  useEffect(
    () =>
      onChange({
        mediaEntriesToDelete,
        newFiles: filesWithPreview.map((fileWithPreview) => fileWithPreview.file),
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filesWithPreview, mediaEntriesToDelete]
  );

  // Get all passed media entries that the user has not selected for deletion
  function getMediaEntriesToDisplay() {
    return mediaEntries.filter((mediaEntry: IMediaEntry) => {
      return !mediaEntriesToDelete.some((deleted) => deleted.id === mediaEntry.id);
    });
  }

  // Render thumbnails for existing media entries and uploaded files
  function renderThumbnails() {
    const mediaEntriesToDisplay = getMediaEntriesToDisplay();

    if (filesWithPreview.length || mediaEntriesToDisplay.length) {
      return (
        <ThumbnailTray>
          {mediaEntriesToDisplay.map((mediaEntry: IMediaEntry) => {
            return renderMediaEntryThumbnail(mediaEntry);
          })}
          {filesWithPreview.map((file: IFileWithPreview) => {
            return renderFileThumbnail(file);
          })}
        </ThumbnailTray>
      );
    } else {
      return null;
    }
  }

  function handleFileLoaderChange(newFilesWithPreview: IFileWithPreview[]) {
    setFilesWithPreview(newFilesWithPreview);
  }

  function handleDeleteMediaEntry(mediaEntry: IMediaEntry) {
    setMediaEntriesToDelete([...mediaEntriesToDelete, mediaEntry]);
  }

  function handleDeleteFile(fileWithPreview: IFileWithPreview) {
    setFilesWithPreview(
      filesWithPreview.filter((file) => {
        return file.preview !== fileWithPreview.preview;
      })
    );
  }

  function renderMediaEntryThumbnail(mediaEntry: IMediaEntry) {
    const button = deleteButton(() => handleDeleteMediaEntry(mediaEntry));

    return (
      <div className="watermark" key={mediaEntry.id}>
        <Thumbnail
          key={mediaEntry.id}
          fileUrl={mediaEntry.entryType == MediaEntryType.VIDEO ? mediaEntry.videoThumbFileUrl : mediaEntry.fileUrl}
          dockTemplate={button}
        />
        {mediaEntry.entryType === MediaEntryType.VIDEO && mediaEntry.metaData && (
          <div className="watermark-timestamp pl-3">
            <Row className={'watermark-timestamp-content'}>
              {getVideoTimeStringfromSeconds(JSON.parse(mediaEntry.metaData).duration as string)}
            </Row>
          </div>
        )}
      </div>
    );
  }

  function renderFileThumbnail(fileWithPreview: IFileWithPreview) {
    const button = deleteButton(() => handleDeleteFile(fileWithPreview));

    return <Thumbnail key={fileWithPreview.preview} fileUrl={fileWithPreview.preview} dockTemplate={button} />;
  }

  function deleteButton(onClick: () => void) {
    return (
      <Tray className="p-1">
        <Button size={Size.SMALL} type="icon" theme={Theme.DARK} onClick={onClick}>
          <Icon svg={DeleteIcon} />
        </Button>
      </Tray>
    );
  }

  return (
    <FileUploader
      filesWithPreview={filesWithPreview}
      header={renderThumbnails()}
      onChange={handleFileLoaderChange}
      className={classes}
      message={message}
      multiple={multiple}
      {...other}
    />
  );
}

export default MediaEntryUploader;
